import { ref } from 'vue';
import { Device } from '@capacitor/device';
import axios from 'axios';
import { getAppVersion } from '@/utils/app-version';
import { isNativeNotWeb } from '@/utils/platform-check';

/**
 * Composable for version checking and force update functionality
 * Supports dynamic version override from remote config for testing
 *
 * @returns {Object} Version check state and methods
 */
export function useVersionCheck() {
  const updateRequired = ref(false);
  const updateMessage = ref('');
  const storeUrl = ref('');
  const minimumVersion = ref('');
  const isChecking = ref(false);
  const checkError = ref(null);

  /**
   * Get → current app version and platform
   * @returns {Promise<{version: string, platform: string, bundleVersion: string|null}>}
   */
  async function getAppInfo() {
    try {
      // getAppVersion() memoises both the binary and bundle version, so this
      // costs no extra bridge calls on a path that gates splash dismissal.
      const [{ nativeVersion, bundleVersion }, deviceInfo] = await Promise.all([
        getAppVersion(),
        Device.getInfo(),
      ]);

      let platform = 'ios';
      if (deviceInfo.platform === 'android') {
        platform = 'android';
      }

      // Use override version from env var if set (for testing force update)
      const overrideVersion = process.env.VUE_APP_FORCE_UPDATE_VERSION;
      const version = overrideVersion || nativeVersion;

      return {
        version,
        platform,
        bundleVersion,
      };
    } catch (error) {
      console.error('[VersionCheck] Error getting app info:', error);
      throw error;
    }
  }

  /**
   * Call → force update API to check if an update is required
   *
   * `currentVersion` is the store binary's version — it does not move when a
   * live update lands, so on its own it sends members to the store for a fix an
   * OTA bundle already delivered.
   *
   * `bundleVersion` is sent so the API *can* tell "needs a new binary" from
   * "needs a new bundle" — but it does not yet. `CheckVersionDto` in
   * hogc-one-api whitelists `platform` and `currentVersion` only, and the
   * global ValidationPipe strips anything else, so this field is discarded
   * server-side until that DTO is extended. Sending it now is harmless and
   * means the client half is ready; the reconciliation is not done.
   *
   * @param {string} platform - 'ios' or 'android'
   * @param {string} currentVersion - Current native app version
   * @param {string|null} [bundleVersion] - Live-update bundle version, if any
   * @returns {Promise<Object>}
   */
  async function callForceUpdateApi(platform, currentVersion, bundleVersion = null) {
    try {
      const apiUrl = process.env.VUE_APP_API_URL || '';
      const response = await axios.post(
        `${apiUrl}/force-update/version-check`,
        {
          platform,
          currentVersion,
          ...(bundleVersion ? { bundleVersion } : {}),
        },
        {
          timeout: 5000, // 5 second timeout for version check
        },
      );
      return response.data;
    } catch (error) {
      console.warn('[VersionCheck] Force update API unavailable; continuing without lockout:', {
        message: error?.message,
        status: error?.response?.status,
      });
      throw error;
    }
  }

  /**
   * Check if an app update is required
   * This should be called on app launch
   *
   * @returns {Promise<Object>} Result object with success flag and update info
   */
  async function checkForUpdate() {
    // Force update is about the STORE BINARY. The web build has none: it always
    // serves the newest JS, it cannot be sent to an app store, and its
    // nativeVersion falls through to the literal 'web' (app-version.js), which
    // is not semver. Posting it was ~17 rejected calls a day, every day since
    // the feature shipped — hogc-one-api#1239.
    if (!isNativeNotWeb()) {
      return { success: true, updateRequired: false, skipped: true };
    }

    if (isChecking.value) {
      return { success: false, alreadyChecking: true };
    }

    isChecking.value = true;
    checkError.value = null;

    try {
      // Get app info
      const { version, platform, bundleVersion } = await getAppInfo();

      // Call force update API
      const result = await callForceUpdateApi(platform, version, bundleVersion);

      // Update state
      updateRequired.value = result.updateRequired || false;
      updateMessage.value = result.message || '';
      storeUrl.value = result.storeUrl || '';
      minimumVersion.value = result.minimumVersion || '';

      // Explicitly log which version will be used
      const finalVersion =
        result.updateRequired && result.minimumVersion ? result.minimumVersion : version;

      console.log('[VersionCheck] Version check completed:', {
        version: finalVersion,
        bundleVersion,
        platform,
        updateRequired: updateRequired.value,
        overridden: !!process.env.VUE_APP_FORCE_UPDATE_VERSION,
      });

      return {
        success: true,
        updateRequired: updateRequired.value,
        message: updateMessage.value,
        storeUrl: storeUrl.value,
        minimumVersion: minimumVersion.value,
      };
    } catch (error) {
      // Graceful degradation - on error, assume no update required
      // This prevents app lockout due to network issues
      console.warn('[VersionCheck] Version check failed, allowing app to continue:', error.message);
      checkError.value = error;
      // Reset update state to allow app to function
      updateRequired.value = false;
      return {
        success: false,
        error: error.message,
        updateRequired: false,
      };
    } finally {
      isChecking.value = false;
    }
  }

  /**
   * Reset → version check state
   * Useful for testing or after an update is completed
   */
  function resetState() {
    updateRequired.value = false;
    updateMessage.value = '';
    storeUrl.value = '';
    minimumVersion.value = '';
    checkError.value = null;
  }

  /**
   * Open → app store URL
   * Uses Browser capacitor plugin for deep linking
   */
  async function openStore() {
    if (!storeUrl.value) {
      console.warn('[VersionCheck] No store URL available');
      return;
    }

    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: storeUrl.value });
    } catch (error) {
      console.error('[VersionCheck] Error opening store:', error);
      // Fallback: use window.location
      if (typeof window !== 'undefined') {
        window.location.href = storeUrl.value;
      }
    }
  }

  return {
    // State
    updateRequired,
    updateMessage,
    storeUrl,
    minimumVersion,
    isChecking,
    checkError,

    // Methods
    checkForUpdate,
    getAppInfo,
    openStore,
    resetState,
  };
}
